import { Spinner } from '@ibsys/kit';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  asChartRuleError,
  useCustomChartData,
  type CustomChartDataParams,
  type CustomChartDefinition,
  type IndicatorSelectHandler,
} from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { chartBindingDataParams, type CustomChartBindingsState } from '../../model/useCustomChartBindings';
import { ChartCardHeaderProvider } from './ChartCardHeaderContext';
import { ChartCardShell } from './ChartCardShell';
import type { ChartAppearance } from './chartElements';
import { ChartSliceSwitcher } from './ChartSliceSwitcher';
import { CustomChartView } from './CustomChartView';

/** Фильтры панели дашборда, общие для всех диаграмм пресета. */
export type CustomChartPanelFilters = Omit<
  CustomChartDataParams,
  'chartType' | 'rule' | 'signature' | 'bindings' | 'bindingOptionQueries'
>;

type Props = {
  chart: CustomChartDefinition;
  filters: CustomChartPanelFilters;
  /** Пороги зон качества пользователя: по ним окрашиваются элементы со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Разрезы и выбранное; не заданы — диаграмма наполняется одними фильтрами панели */
  bindings?: CustomChartBindingsState;
  /** Переход к диалогам выбранного элемента; не задан — содержимое переходов не выполняет */
  onSelect?: IndicatorSelectHandler;
  /** Открытие одного диалога; не задано — содержимое переходов не выполняет */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Диаграмма пользовательского дашборда: наполняется по своему правилу, общим фильтрам панели и
 * значениям своих мест подстановки — выбранному разрезу и выбранному в ведущей диаграмме.
 *
 * Описание при показе заново не истолковывается — правило закреплено за диаграммой ещё в её
 * свойствах. Отказ по правилу показывается в самом блоке: остальные диаграммы дашборда
 * наполняются как обычно.
 *
 * Диаграмма, записанная прежним составом правила (без запроса), наполнению не подлежит: её
 * нужно пересобрать. Об этом сказано в её блоке, и к бэкенду за ней не ходят — отказ был бы
 * тем же самым, только неявным.
 *
 * Связанная диаграмма до выбора в ведущей показывает подсказку выбрать, а не пустоту: пустой блок
 * читался бы как «данных нет», хотя их просто не о чем спрашивать.
 *
 * Переходы к диалогам бывают погашены — карточка получает их незаданными и передаёт содержимому
 * как есть: тогда оно и не переходит, и нажимаемым не показано.
 */
export const CustomChartCard: FC<Props> = ({ chart, filters, zoneRanges, bindings, onSelect, onOpenConversation }) => {
  const { t } = useTranslation('qualityDashboard');
  // Оформление идёт мимо наполнения: смена цветов и направления перерисовывает уже полученные
  // данные, не давая нового ключа кэша. Собрано один раз на состав диаграммы — представлению оно
  // достаётся тем же значением, пока состав не правили.
  const appearance = useMemo(
    (): ChartAppearance => ({
      barOrientation: chart.barOrientation,
      categoryColors: chart.categoryColors,
      valueColorRules: chart.valueColorRules,
    }),
    [chart.barOrientation, chart.categoryColors, chart.valueColorRules],
  );
  const isRuleOutdated = !chart.rule?.query;
  const bindingValues = bindings ? bindings.values : {};
  const isAwaitingSelection = bindingValues === null;

  const { data, isLoading, error } = useCustomChartData(
    isRuleOutdated || bindingValues === null
      ? null
      : {
          chartType: chart.chartType,
          rule: chart.rule,
          signature: chart.signature || null,
          ...filters,
          ...chartBindingDataParams(chart, bindingValues),
        },
  );

  const sliceSwitcher = bindings?.slice ? (
    <ChartSliceSwitcher
      binding={bindings.slice.binding}
      // Значения разреза, которых описание не перечислило, отбирает наполнение — то самое,
      // которым нарисована эта диаграмма: переключатель показывает её же разрез.
      options={bindings.slice.binding.options ?? data?.bindingOptions?.[bindings.slice.binding.name]}
      value={bindings.slice.value}
      onChange={bindings.slice.onChange}
    />
  ) : null;

  const shell = (content: ReactNode) => (
    <ChartCardHeaderProvider action={sliceSwitcher}>
      <ChartCardShell title={chart.title}>{content}</ChartCardShell>
    </ChartCardHeaderProvider>
  );

  if (isRuleOutdated) {
    return shell(<p className="text-sm text-muted-foreground">{t('presets.chart.outdatedRule')}</p>);
  }

  if (isAwaitingSelection) {
    return shell(<p className="text-sm text-muted-foreground">{t('presets.chart.awaitingSelection')}</p>);
  }

  if (isLoading) {
    return shell(
      <div className="flex justify-center py-8">
        <Spinner className="size-6" />
      </div>,
    );
  }

  if (error) {
    const ruleError = asChartRuleError(error);
    return shell(<p className="text-sm text-destructive">{ruleError?.message || t('presets.chart.loadFailed')}</p>);
  }

  if (!data || data.total === 0) {
    return shell(<EmptyState />);
  }

  return (
    <ChartCardHeaderProvider action={sliceSwitcher}>
      <CustomChartView
        chartId={chart.id}
        title={chart.title}
        data={data}
        zoneRanges={zoneRanges}
        appearance={appearance}
        hasFixedHeight={chart.height !== undefined}
        onSelect={onSelect}
        onOpenConversation={onOpenConversation}
        onSelectElement={bindings?.onSelectElement}
        selectedElement={bindings?.selectedElement}
      />
    </ChartCardHeaderProvider>
  );
};
